import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Picture, Button } from '@bwoty-web/styleguide';
import { connect } from 'react-redux';
import MatchingBadges from '../matchingBadges/matchingBadges';
import CardLink from '../cardLink/cardLink';
import { tipTypes } from '../../../appSettings';
import { getCloudinarySources } from '../../../utils/imageUtils';
import { createAnchor, handleAnchor } from '../../../utils/helpers';
import { logProductClickEvent } from '../../../utils/tracking';
import './tipCard.scss';

const TipCard = ({
  imageRelativeUrl,
  type,
  title,
  subTitle,
  text,
  url,
  itemClassifications,
  imageDomain,
  labels,
  trackingProduct,
  filters,
  classifications,
}) => {
  const tipCardClasses = classnames(
    'vf-result-card',
    'vf-result-tip-card',
    `vf-result-tip-card--${type}`,
  );

  let tipCardTypeText = '';
  switch (type) {
    case tipTypes.DESTINATION:
      tipCardTypeText = labels.tipTypeDestinationName;
      break;
    case tipTypes.EXCURSION:
      tipCardTypeText = labels.tipTypeExcursionName;
      break;
    default:
      tipCardTypeText = 'unknown';
  }

  const getImage = () => {
    const checkedImageRelativeUrl =
      imageRelativeUrl || labels.fallbackGroupTripImage;
    return (
      <Picture
        className="vf-result-tip-card__image"
        alt={title}
        sources={[
          ...getCloudinarySources(
            '(max-width: 375px)',
            imageDomain,
            checkedImageRelativeUrl,
            250,
            375,
          ),
          ...getCloudinarySources(
            '(max-width: 415px) and (min-width: 376px)',
            imageDomain,
            checkedImageRelativeUrl,
            276,
            414,
          ),
          ...getCloudinarySources(
            '(min-width: 416px) and (max-width: 767px)',
            imageDomain,
            checkedImageRelativeUrl,
            511,
            767,
          ),
          ...getCloudinarySources(
            '(min-width: 768px)',
            imageDomain,
            checkedImageRelativeUrl,
            285,
            429,
          ),
        ]}
      />
    );
  };

  const onClick = () => {
    handleAnchor(title);
    logProductClickEvent(trackingProduct);
  };

  return (
    <div id={createAnchor(title)} className={tipCardClasses}>
      <div className="vf-result-tip-card__tip-container">
        <a
          className="vf-result-tip-card__mobile-tip-type"
          onClick={onClick}
          href={url}
        >
          {`${labels.tipLabel} ${tipCardTypeText}`}
        </a>

        <div className="vf-result-tip-card__image-container">
          <CardLink
            anchorName={title}
            url={url}
            trackingProduct={trackingProduct}
          >
            {getImage()}
          </CardLink>
          <MatchingBadges itemClassifications={itemClassifications} filters={filters} classifications={classifications} />
        </div>

        <div className="vf-result-tip-card__tip-info">
          <a
            className="vf-result-tip-card__tip-type"
            onClick={onClick}
            href={url}
          >
            {`${labels.tipLabel} ${tipCardTypeText}`}
          </a>
          <a className="vf-result-tip-card__title" onClick={onClick} href={url}>
            {title}
          </a>
          <div className="vf-result-tip-card__subtitle">{subTitle}</div>
          <div className="vf-result-tip-card__description">{text}</div>

          <CardLink
            anchorName={title}
            url={url}
            trackingProduct={trackingProduct}
          >
            <Button
              theme="primary"
              className="vf-result-tip-card__button"
              href={url}
            >
              {labels.buttonText}
            </Button>
          </CardLink>
        </div>
      </div>
    </div>
  );
};

TipCard.propTypes = {
  imageDomain: PropTypes.string.isRequired,
  imageRelativeUrl: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  itemClassifications: PropTypes.arrayOf(PropTypes.string).isRequired,
  labels: PropTypes.shape({
    tipLabel: PropTypes.string.isRequired,
    tipTypeDestinationName: PropTypes.string.isRequired,
    tipTypeExcursionName: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    fallbackGroupTripImage: PropTypes.string.isRequired,
  }).isRequired,
  trackingProduct: PropTypes.shape({}).isRequired,
};

const mapStateToProps = ({ siteSettings }) => ({
  filters: siteSettings.filters,
  classifications: siteSettings.classifications,
});

const MemoizedTipCard = React.memo(TipCard, (prevProps, newProps) => {
  // only do a rerender if objectID changes
  return prevProps.objectID === newProps.objectID;
});

export default connect(mapStateToProps)(MemoizedTipCard);
